import { useApolloClient } from '@apollo/client';
import { hideOneMindBot } from '@thoughtspot/1mind';
import { isOrgsOnFTEnabled } from '@thoughtspot/feature-service';
import { useTranslation } from '@thoughtspot/i18n';
import { collectEvent, UserAction, userActionTypeEnum, } from '@thoughtspot/metrics';
import { AccordionList, } from '@thoughtspot/radiant-react/widgets/accordion';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { SegmentControl } from '@thoughtspot/radiant-react/widgets/segment-control';
import { SegmentedControlItem } from '@thoughtspot/radiant-react/widgets/segment-control/segment-control-item';
import { SideNavigation, } from '@thoughtspot/radiant-react/widgets/side-navigation';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import { getCurrentOrgId, hasPrivilege, is1MindBotEnabled, isAdminModernExperienceEnabled, isAdminRoutingEnabled, isComsEnabled, isCopilotEnabled, isCustomChartEnabled, isCustomizeEmailFromAdminEnabled, isCustomRegionEnabled, isModularHomeExperienceEnabled, isOrgUiEnabled, isReactshellEnabled, isSaaSCluster, isVariablesUIEnabled, PRIVILEGES, } from '@thoughtspot/session-service';
import { isOktaEnabled } from '@thoughtspot/system-config-service';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useBlinkContextForAdmin } from "../../contexts/admin-blink-context";
import { BasePageContext } from "../../contexts/basepage-context";
import { useAdminContext, useAdminEditedPageLeaveContext, } from "../../contexts/context";
import { GetSidebarConfigDocument } from "../../services/generated/graphql-types";
import { useGetUserAccessibleOrgsLazyQuery, useSwitchOrgMutation, } from "../../services/session/session-service";
import { useGetSidebarConfigLazy, useGetStatusLazy, } from '../../services/sidebar/sidebar-service';
import { ADMIN_CONTENT_READY_EVENT, ADMIN_RESET_EVENT, CUSTOMIZATION_SCREEN_IDS, } from '../../utils/admin-route-constants';
import { buildAdminUrl, parseAdminRoute } from '../../utils/admin-route-utils';
import { Horizontal, Vertical } from '../../utils/layouts';
import { getSideBarConfig, getSidebarSectionsFromResponse, isNavigationHubActive, showQuickLinks, sidebarTabsMapping, } from '../../utils/sidebar-config';
import styles from './sidebar.module.scss';
export const CONTEXT = {
    ALL: { id: 'all-org', value: '-1', view_mode: 'all' },
    PRIMARY: { id: 'primary-org', value: '0', view_mode: 'primary' },
    NONE: { id: 'none-org', value: '', view_mode: '' },
};
const TAB_SELECTOR_DEBOUNCE_THRESHOLD = 800;
export const Sidebar = (props) => {
    const location = useLocation();
    const navState = location.state;
    const [tabs, setTabs] = useState({});
    const [activeTab, setActiveTab] = useState({});
    const [expandedItems, setExpandedItems] = useState([]);
    const pendingTabIdRef = useRef(null);
    const [isSwitchOrgTabVisible, setIsSwitchOrgTabVisible] = useState(false);
    const oktaEnabled = isOktaEnabled();
    const ctx = useAdminEditedPageLeaveContext();
    const copilotEnabled = isCopilotEnabled();
    const isSwitchedOrg = useRef({
        isSwitched: false,
        isTabClicked: false,
    });
    const tabsChecked = useRef(false);
    const isHandlingMfaAcceptance = useRef(false);
    const isInitialMount = useRef(isAdminRoutingEnabled());
    const { t } = useTranslation();
    const V1_SEGMENT = isReactshellEnabled() ? '/v1' : '';
    const { navService } = useBlinkContextForAdmin();
    const apolloClient = useApolloClient();
    const isModernAdminNav = isAdminModernExperienceEnabled();
    const [hasUnsavedChangesFromContent, setHasUnsavedChangesFromContent,] = useState(false);
    useEffect(() => {
        const handleUnsavedChanges = (e) => {
            setHasUnsavedChangesFromContent(e.detail.hasChanges);
        };
        window.addEventListener('admin:unsaved-changes', handleUnsavedChanges);
        return () => {
            window.removeEventListener('admin:unsaved-changes', handleUnsavedChanges);
        };
    }, []);
    const getMfaAcceptedStatus = () => {
        return (navService.readClientQueryParamsFromPath().get('MfaAccepted') ===
            'true');
    };
    const isOnAdminRoute = () => {
        return window.location.hash.includes('/admin');
    };
    const { OneMindModalComponent } = useAdminContext();
    const [showOneMindModal, setShowOneMindModal] = useState(false);
    const [purchaseSuccessful, setPurchaseSuccessful] = useState(false);
    const removeMFAAcceptedParam = () => {
        const fullUrl = window.location.href;
        const [baseUrl] = fullUrl?.split('?MfaAccepted=true');
        const url = new URL(baseUrl);
        window.history.replaceState({}, '', url.toString());
    };
    const getInitialContext = () => {
        if (isAdminRoutingEnabled()) {
            const { context } = parseAdminRoute();
            if (context === 'all-org')
                return CONTEXT.ALL;
            const currentOrgId = getCurrentOrgId();
            if (isOrgUiEnabled() && currentOrgId !== -1 && currentOrgId !== 0) {
                return CONTEXT.NONE;
            }
            return CONTEXT.PRIMARY;
        }
        if (navState?.orgContext === CONTEXT.ALL.id) {
            return CONTEXT.ALL;
        }
        if (navState?.orgContext === CONTEXT.PRIMARY.id) {
            return CONTEXT.PRIMARY;
        }
        const landingOrgContext = sessionStorage.getItem('AdminLandingOrgContext');
        if (landingOrgContext === CONTEXT.ALL.id) {
            return CONTEXT.ALL;
        }
        if (landingOrgContext === CONTEXT.PRIMARY.id) {
            return CONTEXT.PRIMARY;
        }
        const currentOrgId = getCurrentOrgId();
        let currentContext = CONTEXT.NONE;
        if (isOrgUiEnabled()) {
            if (currentOrgId === -1) {
                currentContext = CONTEXT.ALL;
            }
            else if (currentOrgId === 0) {
                currentContext = CONTEXT.PRIMARY;
            }
        }
        return currentContext;
    };
    const [selectedContext, setSelectedContext] = useState(getInitialContext());
    const [SideBarConfig, setSideBarConfig] = React.useState(isModernAdminNav
        ? []
        : getSideBarConfig(t, tabs, oktaEnabled, false, selectedContext.id === CONTEXT.ALL.id, copilotEnabled, V1_SEGMENT));
    const { teamComponents, quickLinkContainer } = BasePageContext();
    const lastNavigatedIdRef = useRef(null);
    const handleActiveTab = (currentTab) => {
        setActiveTab(currentTab);
        if (currentTab?.item?.id) {
            lastNavigatedIdRef.current = currentTab.item.id;
        }
    };
    const signalSidebarReady = (approvedScreenID) => {
        window.adminApprovedScreenID = approvedScreenID ?? null;
        window.adminSidebarReady = true;
        window.adminAppReadyCallback?.(approvedScreenID);
    };
    const { error: orgError, data: orgsList, loading: orgsLoading, getUserAccessibleOrgsLazy, } = useGetUserAccessibleOrgsLazyQuery();
    const { data, loading, error, getStatus } = useGetStatusLazy();
    const { data: sidebarConfigData, loading: sidebarConfigLoading, error: sidebarConfigError, getSidebarConfig, } = useGetSidebarConfigLazy();
    const [otherViewHasSections, setOtherViewHasSections] = useState(false);
    const activeLoading = isModernAdminNav ? sidebarConfigLoading : loading;
    const getCurrentItem = (id) => {
        const allTabsValue = SideBarConfig;
        let itemFound;
        let currentIndex;
        allTabsValue.forEach((sideBarItem, index) => {
            sideBarItem.navItems.forEach((item) => {
                if (item.id === id) {
                    itemFound = item;
                    currentIndex = index;
                }
            });
        });
        return { item: itemFound, index: currentIndex };
    };
    const getViewMode = () => {
        if (selectedContext.id === CONTEXT.ALL.id) {
            return CONTEXT.ALL.view_mode;
        }
        if (selectedContext.id === CONTEXT.PRIMARY.id) {
            return CONTEXT.PRIMARY.view_mode;
        }
        return '';
    };
    const handleOnClick = (selectedItem, subTabSlug, pushHistory = false) => {
        const { item, index } = getCurrentItem(selectedItem);
        if (item === undefined) {
            if (typeof selectedItem === 'string') {
                if (SideBarConfig.length === 0) {
                    pendingTabIdRef.current = selectedItem;
                    if (!isAdminRoutingEnabled()) {
                        sessionStorage.setItem('AdminLandingTabId', selectedItem);
                    }
                }
                else if (!isAdminRoutingEnabled()) {
                    props.navigateTo(selectedItem, false);
                    const parent = getCurrentItem('customisation').item
                        ? getCurrentItem('customisation')
                        : getCurrentItem('settings-style-customization-react');
                    if (parent.item) {
                        handleActiveTab({
                            item: parent.item,
                            index: parent.index,
                        });
                    }
                }
                else {
                    const parent = getCurrentItem('customisation').item
                        ? getCurrentItem('customisation')
                        : getCurrentItem('settings-style-customization-react');
                    const parentId = parent.item?.id;
                    if (parentId) {
                        const context = selectedContext.id === CONTEXT.ALL.id
                            ? 'all-org'
                            : 'primary-org';
                        navService.goToPath(buildAdminUrl(selectedItem, context).replace(/^#/, ''), { replace: !pushHistory });
                        handleActiveTab({
                            item: parent.item,
                            index: parent.index,
                        });
                        signalSidebarReady(parentId);
                    }
                    else {
                        const firstIdx = SideBarConfig.findIndex((s) => s.navItems?.length);
                        const firstItem = SideBarConfig[firstIdx]?.navItems?.[0];
                        if (firstItem) {
                            handleOnClick(firstItem.id);
                        }
                        else {
                            navService.goToPath('/home');
                        }
                    }
                }
            }
            return;
        }
        pendingTabIdRef.current = null;
        const hasUnsavedChanges = ctx.edit || hasUnsavedChangesFromContent;
        if (hasUnsavedChanges &&
            activeTab.item?.label === item.label) {
            return;
        }
        if (!hasUnsavedChanges) {
            if (item.shouldOpenGetStartedWithTEUrl) {
                window.open(item.url, '_blank');
                return;
            }
            if (item.shouldOpenIntercom) {
                if (is1MindBotEnabled()) {
                    hideOneMindBot(true);
                    setShowOneMindModal(true);
                    return;
                }
                window.Intercom('show');
                return;
            }
            if (item.url) {
                props.navigateTo(item.url, true);
            }
            else if (isAdminRoutingEnabled()) {
                const context = selectedContext.id === CONTEXT.ALL.id
                    ? 'all-org'
                    : 'primary-org';
                const baseUrl = buildAdminUrl(item.id, context).replace(/^#/, '');
                navService.goToPath(subTabSlug ? `${baseUrl}/${subTabSlug}` : baseUrl, { replace: !pushHistory });
                signalSidebarReady(item.id);
            }
            else {
                props.navigateTo(item.id, false);
            }
            handleActiveTab({ item, index });
        }
        else {
            ctx.onUnSaveClick();
        }
    };
    const onTabSelect = (selected) => {
        if (getMfaAcceptedStatus() && !isHandlingMfaAcceptance.current) {
            removeMFAAcceptedParam();
        }
        const hasUnsavedChanges = ctx.edit || hasUnsavedChangesFromContent;
        if (isInitialMount.current && selectedContext.id === selected.id) {
            return;
        }
        if (hasUnsavedChanges && selectedContext.id === selected.id) {
            return;
        }
        if (!hasUnsavedChanges) {
            isSwitchedOrg.current.isSwitched = false;
            isSwitchedOrg.current.isTabClicked = true;
            const userAction = new UserAction(userActionTypeEnum.ORG_TAB);
            collectEvent(userAction, {
                timestamp: Date.now(),
                toOrgId: Number(selected.value),
                toOrgName: selected.view_mode,
            });
            setSelectedContext(selected);
            setActiveTab({});
            props.onTabChange(false, Number(selected.value), selected.id);
        }
        else {
            ctx.onUnSaveClick();
        }
    };
    useEffect(() => {
        const handleCommandPaletteNav = (e) => {
            const { commandId, orgContext, subTabSlug } = e.detail;
            if (orgContext && orgContext !== selectedContext.id) {
                if (!isAdminRoutingEnabled()) {
                    sessionStorage.setItem('AdminLandingTabId', commandId);
                }
                onTabSelect(orgContext === CONTEXT.ALL.id
                    ? CONTEXT.ALL
                    : CONTEXT.PRIMARY);
                return;
            }
            if (commandId) {
                handleOnClick(commandId, subTabSlug);
            }
            else {
                const firstIdx = SideBarConfig.findIndex((s) => s.navItems?.length);
                const firstItem = SideBarConfig[firstIdx]?.navItems?.[0];
                if (firstItem) {
                    setActiveTab({
                        item: firstItem,
                        index: firstIdx,
                    });
                    handleOnClick(firstItem.id);
                }
            }
        };
        window.addEventListener('admin:command-palette-navigate', handleCommandPaletteNav);
        return () => {
            window.removeEventListener('admin:command-palette-navigate', handleCommandPaletteNav);
        };
    }, [SideBarConfig, activeTab, selectedContext]);
    const orgsEnabled = isOrgUiEnabled() &&
        (getCurrentOrgId() === -1 || getCurrentOrgId() === 0);
    const showTabForSwitchOrgV3 = () => {
        if (!orgsEnabled)
            return false;
        if (isModernAdminNav) {
            const currentHasSections = (sidebarConfigData?.Data?.sections?.length ?? 0) > 0;
            return currentHasSections && otherViewHasSections;
        }
        return true;
    };
    const showTabForSwitchOrg = () => {
        if (orgsEnabled && !error && data) {
            return data?.bothTabsDisplayed;
        }
        return showTabForSwitchOrgV3();
    };
    useEffect(() => {
        try {
            if (isModernAdminNav) {
                getSidebarConfig({
                    variables: { viewMode: getViewMode() },
                });
                if (orgsEnabled) {
                    const otherView = getViewMode() === 'all' ? 'primary' : 'all';
                    apolloClient
                        .query({
                        query: GetSidebarConfigDocument,
                        variables: { viewMode: otherView },
                        fetchPolicy: 'no-cache',
                    })
                        .then((result) => {
                        const sections = result?.data?.Admin_getSidebarConfig?.Data
                            ?.sections ?? [];
                        setOtherViewHasSections(sections.length > 0);
                        return result;
                    })
                        .catch(() => setOtherViewHasSections(false));
                }
            }
            else {
                const isCommandKAllOrgNav = sessionStorage.getItem('AdminLandingOrgContext') ===
                    CONTEXT.ALL.id && getCurrentOrgId() === 0;
                const isRoutingAllOrgNav = isAdminRoutingEnabled() &&
                    selectedContext.id === CONTEXT.ALL.id &&
                    getCurrentOrgId() === 0;
                getStatus({
                    variables: {
                        viewMode: getViewMode(),
                        ...(!(isCommandKAllOrgNav || isRoutingAllOrgNav) && {
                            orgsEnabled,
                        }),
                    },
                });
            }
        }
        catch (err) {
            tabsChecked.current = true;
        }
        if (isOrgUiEnabled()) {
            getUserAccessibleOrgsLazy();
        }
    }, []);
    const setDefaultState = (isSwitchOrgTabVisible) => {
        if (isSwitchOrgTabVisible) {
            if (selectedContext.id === CONTEXT.ALL.id) {
                setTabs({
                    status: {
                        org_management: { org: true },
                        user_management: { users: true },
                        authentication: { saml: true },
                        application_settings: {
                            homepage_and_discovery: isModularHomeExperienceEnabled(),
                            email_customizations: isComsEnabled() &&
                                isCustomizeEmailFromAdminEnabled(),
                            variables: hasPrivilege(PRIVILEGES.ORG_ADMINISTRATION) &&
                                isVariablesUIEnabled(),
                        },
                    },
                });
            }
            else if (selectedContext.id === CONTEXT.PRIMARY.id) {
                setTabs({
                    status: {
                        user_management: {
                            users: true,
                            groups: true,
                            roles: true,
                        },
                        application_settings: {
                            chart_customization: isCustomChartEnabled() ||
                                isCustomRegionEnabled(),
                            homepage_and_discovery: isModularHomeExperienceEnabled(),
                            email_customizations: isComsEnabled() &&
                                isCustomizeEmailFromAdminEnabled(),
                        },
                    },
                });
            }
        }
        else if (orgsEnabled) {
            setTabs({
                status: {
                    user_management: {
                        users: true,
                        groups: true,
                        roles: true,
                    },
                    org_management: hasPrivilege(PRIVILEGES.ORG_ADMINISTRATION)
                        ? { org: true }
                        : { org: false },
                    application_settings: {
                        chart_customization: isCustomChartEnabled() || isCustomRegionEnabled(),
                        homepage_and_discovery: isModularHomeExperienceEnabled(),
                        email_customizations: isComsEnabled() &&
                            isCustomizeEmailFromAdminEnabled(),
                        variables: hasPrivilege(PRIVILEGES.ORG_ADMINISTRATION) &&
                            isVariablesUIEnabled() &&
                            isOrgUiEnabled() &&
                            getCurrentOrgId() === -1,
                    },
                },
            });
        }
        else {
            setTabs({
                status: {
                    user_management: {
                        users: true,
                        groups: true,
                        roles: true,
                    },
                    teams: {
                        pricing: true,
                    },
                    application_settings: {
                        chart_customization: isCustomChartEnabled() || isCustomRegionEnabled(),
                        homepage_and_discovery: isModularHomeExperienceEnabled(),
                        email_customizations: isComsEnabled() &&
                            isCustomizeEmailFromAdminEnabled(),
                        variables: hasPrivilege(PRIVILEGES.ORG_ADMINISTRATION) &&
                            isVariablesUIEnabled() &&
                            isOrgUiEnabled() &&
                            getCurrentOrgId() === -1,
                    },
                },
            });
        }
    };
    const fetchTabs = async () => {
        if (isModernAdminNav) {
            if (!sidebarConfigError && !sidebarConfigData)
                return;
            const isMfaAccepted = getMfaAcceptedStatus();
            if (!tabsChecked.current) {
                tabsChecked.current = true;
            }
            if (isMfaAccepted) {
                setIsSwitchOrgTabVisible(true);
            }
            else {
                setIsSwitchOrgTabVisible(showTabForSwitchOrgV3());
            }
            if (!sidebarConfigError &&
                !sidebarConfigLoading &&
                sidebarConfigData) {
                setSideBarConfig(getSidebarSectionsFromResponse(t, sidebarConfigData, V1_SEGMENT, purchaseSuccessful));
            }
            return;
        }
        if (!error && !data)
            return;
        const isCommandKAllOrgNav = sessionStorage.getItem('AdminLandingOrgContext') ===
            CONTEXT.ALL.id && getCurrentOrgId() === 0;
        const isRoutingAllOrgNav = isAdminRoutingEnabled() &&
            selectedContext.id === CONTEXT.ALL.id &&
            getCurrentOrgId() === 0;
        const isSwitchOrgTabVisibleLocal = isCommandKAllOrgNav || isRoutingAllOrgNav
            ? showTabForSwitchOrgV3()
            : showTabForSwitchOrg();
        const isMfaAccepted = getMfaAcceptedStatus();
        if (!tabsChecked.current) {
            setIsSwitchOrgTabVisible(isSwitchOrgTabVisibleLocal);
            tabsChecked.current = true;
        }
        if (isMfaAccepted) {
            setIsSwitchOrgTabVisible(true);
        }
        if (!error && !loading && data) {
            const transformedData = {
                status: {
                    ...data?.Data.status,
                    teams: {
                        pricing: true,
                    },
                },
                __typename: data?.Data.__typename,
            };
            setTabs(isOrgsOnFTEnabled() ? transformedData : data?.Data);
        }
        else if (error && !loading && !isSaaSCluster()) {
            setDefaultState(isSwitchOrgTabVisibleLocal);
        }
    };
    const getActiveIndex = (navItem) => {
        const activeId = activeTab.item?.id || lastNavigatedIdRef.current;
        if (!activeId)
            return undefined;
        for (let i = 0; i < navItem.length; i++) {
            if (navItem[i].id === activeId) {
                return i;
            }
        }
        return undefined;
    };
    useEffect(() => {
        fetchTabs();
        if (!activeLoading && !isSwitchedOrg.current.isTabClicked) {
            props.onTabChange(activeLoading, Number(selectedContext.value), selectedContext.id);
        }
    }, [
        data,
        loading,
        error,
        sidebarConfigData,
        sidebarConfigLoading,
        sidebarConfigError,
        purchaseSuccessful,
    ]);
    useEffect(() => {
        if (isModernAdminNav && orgsEnabled) {
            setIsSwitchOrgTabVisible(showTabForSwitchOrgV3());
        }
    }, [otherViewHasSections]);
    const successCallBack = async () => {
        try {
            await getUserAccessibleOrgsLazy();
            if (isSwitchedOrg.current.isTabClicked) {
                isSwitchedOrg.current.isTabClicked = false;
                props.onTabChange(true, Number(selectedContext.value), selectedContext.id);
            }
        }
        catch (err) {
            console.log(error);
        }
        finally {
            if (isModernAdminNav) {
                getSidebarConfig({
                    variables: { viewMode: getViewMode() },
                });
            }
            else {
                getStatus({
                    variables: { viewMode: getViewMode() },
                });
            }
        }
    };
    const onSuccessSwitchOrg = (isSwitched) => {
        successCallBack();
    };
    const [switchOrg, switchOrgObj] = useSwitchOrgMutation(onSuccessSwitchOrg, err => {
    }, selectedContext.value);
    const handleMfaAcceptance = () => {
        setSelectedContext(CONTEXT.ALL);
        handleOnClick(isAdminModernExperienceEnabled()
            ? 'modern-user-management'
            : 'authentication-mfa');
        removeMFAAcceptedParam();
        isHandlingMfaAcceptance.current = false;
    };
    useEffect(() => {
        if (isSwitchOrgTabVisible) {
            successCallBack();
        }
        else if (!isSwitchOrgTabVisible && tabsChecked.current) {
            if (isModernAdminNav) {
                getSidebarConfig({
                    variables: { viewMode: getViewMode() },
                });
            }
            else {
                getStatus({
                    variables: { viewMode: getViewMode() },
                });
            }
        }
    }, [selectedContext]);
    useEffect(() => {
        const isMfaAccepted = getMfaAcceptedStatus();
        if (isMfaAccepted) {
            isHandlingMfaAcceptance.current = true;
            setIsSwitchOrgTabVisible(true);
            setSelectedContext(CONTEXT.ALL);
        }
    }, []);
    useEffect(() => {
        if (isModernAdminNav) {
            return;
        }
        setSideBarConfig(getSideBarConfig(t, tabs, oktaEnabled, false, getMfaAcceptedStatus()
            ? true
            : selectedContext.id === CONTEXT.ALL.id, copilotEnabled, V1_SEGMENT));
    }, [tabs]);
    const prevHashRef = useRef(window.location.hash);
    useEffect(() => {
        const handleHashChange = (fromListener = false) => {
            const currentHash = window.location.hash;
            const prevHash = prevHashRef.current;
            const wasOnAdminRoute = prevHash.includes('/admin');
            const isNavHubActive = isNavigationHubActive();
            if (isNavHubActive &&
                isOnAdminRoute() &&
                !wasOnAdminRoute &&
                SideBarConfig.length > 0 &&
                !_.isEmpty(activeTab)) {
                const urlTabId = isAdminRoutingEnabled()
                    ? parseAdminRoute().tabId
                    : null;
                if (urlTabId) {
                    handleOnClick(urlTabId);
                }
                else {
                    const firstIdx = SideBarConfig.findIndex((s) => s.navItems?.length);
                    const firstItem = SideBarConfig[firstIdx]?.navItems?.[0];
                    if (firstItem) {
                        setActiveTab({
                            item: firstItem,
                            index: firstIdx,
                        });
                        handleOnClick(firstItem.id);
                    }
                    else {
                        setActiveTab({});
                    }
                }
                prevHashRef.current = currentHash;
            }
            else if (isOnAdminRoute()) {
                prevHashRef.current = currentHash;
            }
            else {
                prevHashRef.current = currentHash;
            }
        };
        const handleResetToDefault = () => {
            const currentOrgId = getCurrentOrgId();
            const defaultContext = isOrgUiEnabled() && currentOrgId !== -1 && currentOrgId !== 0
                ? CONTEXT.NONE
                : CONTEXT.PRIMARY;
            const isAlreadyDefault = selectedContext.id === defaultContext.id;
            if (!isAlreadyDefault) {
                setSelectedContext(defaultContext);
                props.onTabChange(false, Number(defaultContext.value), defaultContext.id);
                if (isModernAdminNav) {
                    getSidebarConfig({
                        variables: { viewMode: defaultContext.view_mode },
                    });
                }
            }
            setActiveTab({});
            if (SideBarConfig.length === 0 || !isAlreadyDefault)
                return;
            const firstIdx = SideBarConfig.findIndex((s) => s.navItems?.length);
            const firstItem = SideBarConfig[firstIdx]?.navItems?.[0];
            if (firstItem) {
                setActiveTab({ item: firstItem, index: firstIdx });
                handleOnClick(firstItem.id);
                signalSidebarReady(firstItem.id);
            }
        };
        const handleContentReady = () => {
            if (SideBarConfig.length === 0 || _.isEmpty(activeTab))
                return;
            const current = activeTab.item;
            if (current) {
                handleOnClick(current.id);
            }
        };
        handleHashChange(false);
        const onHashChange = () => handleHashChange(true);
        window.addEventListener('hashchange', onHashChange);
        window.addEventListener(ADMIN_RESET_EVENT, handleResetToDefault);
        window.addEventListener(ADMIN_CONTENT_READY_EVENT, handleContentReady);
        return () => {
            window.removeEventListener('hashchange', onHashChange);
            window.removeEventListener(ADMIN_RESET_EVENT, handleResetToDefault);
            window.removeEventListener(ADMIN_CONTENT_READY_EVENT, handleContentReady);
        };
    }, [SideBarConfig, activeTab]);
    useEffect(() => {
        if (!isAdminRoutingEnabled() ||
            isInitialMount.current ||
            !isOnAdminRoute() ||
            SideBarConfig.length === 0 ||
            _.isEmpty(activeTab)) {
            return;
        }
        const { tabId, context } = parseAdminRoute();
        if (!tabId)
            return;
        const urlIsAllOrg = context === 'all-org';
        const sidebarIsAllOrg = selectedContext.id === CONTEXT.ALL.id;
        if (urlIsAllOrg !== sidebarIsAllOrg) {
            onTabSelect(urlIsAllOrg ? CONTEXT.ALL : CONTEXT.PRIMARY);
            return;
        }
        const found = getCurrentItem(tabId);
        if (found.item) {
            const alreadyActive = activeTab?.item?.id === found.item.id;
            const alreadyApproved = window.adminApprovedScreenID === found.item.id;
            if (alreadyActive && alreadyApproved)
                return;
            handleActiveTab(found);
            signalSidebarReady(found.item.id);
        }
        else if (CUSTOMIZATION_SCREEN_IDS.has(tabId)) {
            handleOnClick(tabId);
        }
        else {
            navService.goToPath('/home');
        }
    }, [location.key]);
    useEffect(() => {
        return () => {
            delete window.adminSidebarReady;
            delete window.adminApprovedScreenID;
        };
    }, []);
    useEffect(() => {
        if (props.onRegisterTabSwitcher) {
            props.onRegisterTabSwitcher(handleOnClick);
        }
        else {
            teamComponents.adminTabSwitcher.registerCb((activatedTabId) => {
                handleOnClick(activatedTabId);
            });
        }
        const configFetched = isModernAdminNav
            ? !!sidebarConfigData && !sidebarConfigLoading
            : !!data && !loading;
        if (SideBarConfig.length === 0 &&
            configFetched &&
            isAdminRoutingEnabled() &&
            isOnAdminRoute()) {
            navService.goToPath('/home');
        }
        if (SideBarConfig.length > 0 && (!activeTab || _.isEmpty(activeTab))) {
            setExpandedItems([...Array(SideBarConfig.length).keys()]);
            if (isModernAdminNav) {
                const isMfaAccepted = getMfaAcceptedStatus();
                if (isMfaAccepted) {
                    setSelectedContext(CONTEXT.ALL);
                    handleMfaAcceptance();
                }
                else {
                    const urlRoute = isAdminRoutingEnabled()
                        ? parseAdminRoute()
                        : null;
                    const urlContextMatchesSidebar = !urlRoute ||
                        (urlRoute.context === 'all-org') ===
                            (selectedContext.id === CONTEXT.ALL.id);
                    const urlTabId = urlContextMatchesSidebar && urlRoute?.tabId
                        ? urlRoute.tabId
                        : null;
                    const sessionStorageLandingTab = urlRoute
                        ? null
                        : sessionStorage.getItem('AdminLandingTabId');
                    const activatedTabId = pendingTabIdRef.current ||
                        urlTabId ||
                        navState?.adminLandingTabId ||
                        sessionStorageLandingTab;
                    pendingTabIdRef.current = null;
                    const foundInConfig = activatedTabId
                        ? getCurrentItem(activatedTabId)
                        : { item: undefined, index: undefined };
                    if (activatedTabId && foundInConfig.item) {
                        if (urlTabId) {
                            handleActiveTab(foundInConfig);
                        }
                        else {
                            handleOnClick(activatedTabId);
                        }
                        sessionStorage.removeItem('AdminLandingTabId');
                        sessionStorage.removeItem('AdminLandingOrgContext');
                        signalSidebarReady(foundInConfig.item.id);
                    }
                    else if (activatedTabId &&
                        CUSTOMIZATION_SCREEN_IDS.has(activatedTabId)) {
                        handleOnClick(activatedTabId);
                        sessionStorage.removeItem('AdminLandingTabId');
                        sessionStorage.removeItem('AdminLandingOrgContext');
                    }
                    else {
                        const firstItemIndex = SideBarConfig.findIndex(section => section.navItems?.length);
                        const firstItem = SideBarConfig[firstItemIndex]?.navItems?.[0];
                        if (firstItem) {
                            setActiveTab({
                                item: firstItem,
                                index: firstItemIndex,
                            });
                            handleOnClick(firstItem.id);
                            signalSidebarReady(firstItem.id);
                        }
                    }
                }
            }
            else {
                const isMfaAccepted = getMfaAcceptedStatus();
                if (isMfaAccepted) {
                    setSelectedContext(CONTEXT.ALL);
                    handleMfaAcceptance();
                }
                else {
                    const urlRoute = isAdminRoutingEnabled()
                        ? parseAdminRoute()
                        : null;
                    const urlContextMatchesSidebar = !urlRoute ||
                        (urlRoute.context === 'all-org') ===
                            (selectedContext.id === CONTEXT.ALL.id);
                    const urlTabId = urlContextMatchesSidebar && urlRoute?.tabId
                        ? urlRoute.tabId
                        : null;
                    const sessionStorageLandingTab = isAdminRoutingEnabled()
                        ? null
                        : sessionStorage.getItem('AdminLandingTabId');
                    const activatedTabId = urlTabId ||
                        navState?.adminLandingTabId ||
                        sessionStorageLandingTab;
                    const foundInConfig = activatedTabId
                        ? getCurrentItem(activatedTabId)
                        : { item: undefined };
                    if (activatedTabId && foundInConfig.item) {
                        const mappedTab = sidebarTabsMapping(t, V1_SEGMENT)[activatedTabId];
                        if (mappedTab) {
                            setActiveTab(mappedTab);
                        }
                        handleOnClick(activatedTabId);
                        sessionStorage.removeItem('AdminLandingTabId');
                        sessionStorage.removeItem('AdminLandingOrgContext');
                        signalSidebarReady(foundInConfig.item.id);
                    }
                    else {
                        const firstIdx = SideBarConfig.findIndex((s) => s.navItems?.length);
                        const firstItem = SideBarConfig[firstIdx]?.navItems?.[0];
                        if (firstItem) {
                            setActiveTab({
                                item: firstItem,
                                index: firstIdx,
                            });
                            handleOnClick(firstItem.id);
                            signalSidebarReady(firstItem.id);
                        }
                    }
                }
            }
        }
        isInitialMount.current = false;
        return () => {
            if (!props.onRegisterTabSwitcher) {
                teamComponents.adminTabSwitcher.deregisterCb();
            }
        };
    }, [SideBarConfig]);
    useEffect(() => {
        const subscription = teamComponents.purchaseCompleteObserver.subscribe(() => {
            if (isModernAdminNav) {
                setPurchaseSuccessful(true);
                getSidebarConfig({
                    variables: { viewMode: getViewMode() },
                });
                return;
            }
            setSideBarConfig(getSideBarConfig(t, tabs, oktaEnabled, true, selectedContext.id === CONTEXT.ALL.id, copilotEnabled, V1_SEGMENT));
        });
        return () => {
            subscription.unsubscribe();
            teamComponents.adminTabSwitcher.deregisterCb();
        };
    }, [tabs]);
    const getLinkBuilderUrl = () => {
        const tempRootPath = '/admin';
        return `#${tempRootPath}`;
    };
    const renderOneMindModal = () => showOneMindModal
        ? OneMindModalComponent({
            showOneMindModal,
            setShowOneMindModal,
        })
        : null;
    {
        const renderNavV3Items = () => {
            return (React.createElement(LoadingIndicator.Global, { hideLoader: !activeLoading, className: styles.loadingIndicator }, !activeLoading && (React.createElement("div", { className: styles.allSections }, SideBarConfig.map(item => {
                const sectionTitle = item.label?.trim();
                const navItems = item.navItems.map((subitem) => ({
                    ...subitem,
                    data: {
                        ...subitem,
                    },
                    isClickable: false,
                }));
                return (navItems.length > 0 && (React.createElement("div", { className: styles.section, key: item.label },
                    sectionTitle ? (React.createElement(Typography, { role: "heading", "aria-level": 1, variant: "p-bold", className: styles.navSectionHeader }, sectionTitle.toUpperCase())) : null,
                    React.createElement(AccordionList, { listData: navItems, activeItemIndex: activeTab?.item ||
                            lastNavigatedIdRef.current
                            ? getActiveIndex(navItems)
                            : undefined, onClick: args => handleOnClick(args, undefined, true), className: styles.navListItem, linkBuilder: getLinkBuilderUrl }))));
            })))));
        };
        const onSelectHandler = (index) => {
            if (index === 0) {
                onTabSelect(CONTEXT.ALL);
            }
            else {
                onTabSelect(CONTEXT.PRIMARY);
            }
        };
        const renderNavV3FacetBar = () => isSwitchOrgTabVisible && (React.createElement(Horizontal, { hAlignContent: "left", className: styles.facetBar },
            React.createElement(SegmentControl, { selectedIndex: selectedContext.id === CONTEXT.ALL.id ? 0 : 1, onSelect: onSelectHandler },
                React.createElement(SegmentedControlItem, { title: t('orgs.adminPanel.allorg'), isThemeEnabled: true, "data-testid": "all-orgs-segment" }),
                React.createElement(SegmentedControlItem, { title: t('orgs.adminPanel.primaryorg'), isThemeEnabled: true, "data-testid": "primary-org-segment" }))));
        return (React.createElement(Vertical, { "data-testid": "nav-component", className: styles.adminMainMenu },
            renderOneMindModal(),
            React.createElement(SideNavigation, { className: styles.sideNavigation },
                React.createElement(Horizontal, { className: styles.tabHeader, vAlignContent: "center" },
                    React.createElement(Typography, { role: "heading", "aria-level": 1, variant: "section-label", "data-testid": "left-nav-bar-v3-title", className: styles.tabTitle }, t('sidebar.header.newNav.title'))),
                renderNavV3FacetBar(),
                renderNavV3Items()),
            showQuickLinks && quickLinkContainer
                ? quickLinkContainer()
                : null));
    }
};
//# sourceMappingURL=sidebar.container.js.map